import { useMemo, useEffect, useState, useCallback } from 'react';
import { Spinner, Icon } from '@iq/react-components';
import { useSelector } from 'react-redux';
import {
  ResponsiveContainer,
  Cell,
  BarChart,
  YAxis,
  XAxis,
  Bar,
  ReferenceLine,
  Legend,
  Tooltip,
} from 'recharts';
import classnames from 'classnames';

import DegradedQualityIndicator from '../shared/DegradedQualityIndicator';
import Loader from '../../../../Loader';
import { getSiteVariablesIndex } from '../../../../../bundles/sources';
import { useClientSize } from '../../../../../utils';
import { THRESHOLD_TYPES } from '../../../../../constants';
import { resolveThresholdItems } from '../../schemas/thresholdSchema';
import { lightenDarkenColor, resolveColor } from '../../../../../colors';
import { format } from '../../../../../units';
import { DropdownMenu } from '../../../../DropdownMenu';
import LabelWithTooltip from '../../../../LabelWithTooltip';

const darkenThresholdColor = (color) => lightenDarkenColor(color, -10);

const CustomLegend = ({ thresholdsData, isLinearThresholdType, isVertical }) => {
  const size = 10;
  const linearHeight = 15;
  const linearWidth = 25;

  const linearThresholdClass = 'bar-gauge-legend-linear-threshold';
  const areaThresholdClass = 'bar-gauge-legend-area-threshold';

  return (
    <ul className="bar-gauge-legend">
      {thresholdsData?.map((thd, index) =>
        thd.description ? (
          <li
            className="bar-gauge-legend-item"
            key={`${thd.fill} + ${index}`}
          >
            <svg
              width={isLinearThresholdType && !isVertical ? linearWidth : size}
              height={isLinearThresholdType ? linearHeight : size}
              className={isLinearThresholdType ? linearThresholdClass : areaThresholdClass}
            >
              {isLinearThresholdType ? (
                <line
                  x1="0"
                  x2={isVertical ? '0' : linearWidth}
                  y1={isVertical ? '0' : linearHeight / 2}
                  y2={isVertical ? '15' : linearHeight / 2}
                  strokeWidth={isVertical ? '5' : '3'}
                  stroke={resolveColor(thd.fill)}
                />
              ) : (
                <rect
                  x="0"
                  y="0"
                  width={size}
                  height={size}
                  fill={`${resolveColor(thd.fill)}40`}
                />
              )}
            </svg>
            <span>{thd.description}</span>
          </li>
        ) : null
      )}
    </ul>
  );
};

const tooltipFormatter = (value, _name, props) => {
  return [props.payload?.formattedValueWithUnit ?? value, props.payload?.name ?? _name];
};

const normalizeArrayLike = (value) => {
  if (Array.isArray(value)) return value;
  if (ArrayBuffer.isView(value)) return [...value];
  if (value && typeof value === 'object') {
    if (Number.isInteger(value.length) && value.length >= 0) {
      return Array.from({ length: value.length }, (_, index) => value[index]);
    }
    return null;
  }

  return null;
};

const parseArrayLike = (value) => {
  const normalizedValue = normalizeArrayLike(value);
  if (normalizedValue) return normalizedValue;
  if (typeof value !== 'string') return null;

  try {
    return normalizeArrayLike(JSON.parse(value));
  } catch {
    return null;
  }
};

const parseNumberArray = (value) => {
  const result = parseArrayLike(value);
  return result ? result.map((entry) => Number.parseFloat(entry)) : null;
};

const BarGaugeVisualization = ({
  visualization,
  onFullscreen,
  isFullscreen,
  isPreview,
  getMoreOptions,
  dropdownPlacement,
  disconnected,
  onSingleSignalTypeErrorChange,
  latestValues: values,
  initialLoaded: initialLoadedOverride,
  loading: loadingOverride,
}) => {
  const variablesIndex = useSelector(getSiteVariablesIndex);
  const [singleSignalTypeError, setSingleSignalTypeError] = useState(null);

  const initialLoaded = initialLoadedOverride ?? Boolean(values);
  const loading = loadingOverride ?? !values;

  const { configuration = {}, variables = [] } = visualization;
  const { singleSignal = false, singleSignalId = null, thresholds = {} } = configuration;

  let sourceValues = values;

  if (singleSignal && singleSignalId) {
    const singleSignalArray = values?.filter((value) => value.variable === singleSignalId) || [];
    const singleSignalValue = singleSignalArray[0]?.value?.[1];
    const parsedSingleSignalValue = parseNumberArray(singleSignalValue);
    const validSingleSignal = singleSignalArray.length === 1 && parsedSingleSignalValue;
    if (validSingleSignal) {
      if (singleSignalTypeError) {
        setSingleSignalTypeError(null);
      }
      sourceValues = singleSignalArray;
    } else {
      // Only show an error when data has actually arrived but the value is not array-like.
      // When singleSignalValue is null/undefined (data not yet loaded), skip to avoid false positives.
      const hasNonArrayData =
        singleSignalArray.length === 1 && singleSignalValue != null && !parsedSingleSignalValue;
      if (hasNonArrayData && !singleSignalTypeError) {
        setSingleSignalTypeError(
          "Unexpected data type. The configured single signal's value is not array-like."
        );
      }
      sourceValues = [];
    }
  } else if (!singleSignal && singleSignalTypeError) {
    setSingleSignalTypeError(null);
  }

  const thresholdType = thresholds.thresholdType || THRESHOLD_TYPES.linear.value;
  const isLinearThresholdType = thresholdType === THRESHOLD_TYPES.linear.value;
  const isVertical = configuration.layout === 'vertical';

  const [{ width, height }, clientReference] = useClientSize();

  useEffect(() => {
    onSingleSignalTypeErrorChange?.(singleSignalTypeError);
  }, [singleSignalTypeError, onSingleSignalTypeErrorChange]);

  const dataMinMax = useMemo(() => {
    if (sourceValues?.length) {
      const allValues = sourceValues.flatMap((signalValue) => {
        const value = signalValue.value?.[1];
        const parsedValues = singleSignal && parseNumberArray(value);
        return parsedValues ?? [Number.parseFloat(value)];
      });
      const numericValues = allValues.filter((value) => !Number.isNaN(value));

      return {
        min: Math.min(...numericValues),
        max: Math.max(...numericValues),
      };
    }
    return {};
  }, [sourceValues, singleSignal]);

  const sortedThresholds = useMemo(
    () => resolveThresholdItems(thresholds, variables, dataMinMax),
    [thresholds, variables, dataMinMax]
  );

  const getThresholdValue = useCallback(
    (item) => {
      if (isLinearThresholdType) return item?.value || 0;
      const min = item?.min || 0;
      const max = item?.max || item?.min || 0;
      return [min, max];
    },
    [isLinearThresholdType]
  );

  const getProcessedValues = useCallback(
    // Expects a datapoint value ([timestamp, value, quality])
    // For single signals with array-like values, we pass in the positional value as
    // the second item in the array (e.g. [timestamp, positionalValue, quality]) to
    // get correct formatting and unit resolution per position
    (variableConfig, variableId, value) => {
      // unit/unitLabel are per-signal here (unlike grouped-bar, which resolves them from configuration)
      const { unit } = variableConfig || {};
      const signalUnit = variablesIndex?.[variableId]?.unit;
      const unitLabel =
        unit !== 'variable-default' && !variableConfig?.unitLabel
          ? unit
          : variableConfig?.unitLabel;
      const displayUnit = unit === 'variable-default' ? signalUnit || '' : unit;
      if (!Array.isArray(value) || value[1] == null || Number.isNaN(Number(value[1]))) {
        return {
          ...format(displayUnit, null, configuration.decimals, unitLabel),
          rawValue: null,
          quality: 2,
        };
      }
      const coerced = Number(value[1]);
      const rawValue =
        configuration.decimals || configuration.decimals === 0
          ? coerced.toFixed(configuration.decimals)
          : coerced;

      return {
        ...format(displayUnit, rawValue, configuration.decimals, unitLabel),
        rawValue,
        quality: value[2] ?? 2,
      };
    },
    [variablesIndex, configuration.decimals]
  );

  // Transform data for chart - handles both array signals and regular signals
  const data = useMemo(() => {
    if (!sourceValues || !initialLoaded) {
      return [];
    }

    if (singleSignal && sourceValues.length === 1) {
      const { value: singleSignalValue, variable: singleSignalId } = sourceValues[0];
      const variableConfig = variables.find((v) => v.id === singleSignalId);
      const parsedArrayValues = parseNumberArray(singleSignalValue?.[1]) || [];
      // Array signal: create one bar per position
      // const quality = singleSignalValue?.[2] ?? 1;
      return parsedArrayValues.map((positionalValue, index) => {
        const {
          value: formattedValue,
          unit: resolvedUnit,
          quality,
        } = getProcessedValues(variableConfig, singleSignalId, [
          singleSignalValue?.[0],
          positionalValue,
          singleSignalValue?.[2],
        ]);

        return {
          name: `${index + 1}`,
          value: positionalValue,
          unit: resolvedUnit,
          formattedValue,
          quality,
        };
      });
    }

    // Regular signals: existing logic
    const result = [];
    for (const { variable, value } of sourceValues) {
      const variableConfig = variables.find((v) => v.id === variable);
      const { label, name } = variableConfig || {};

      const {
        value: formattedValue,
        unit: resolvedUnit,
        rawValue,
        quality,
      } = getProcessedValues(variableConfig, variable, value);

      result.push({
        name: label || name || variable,
        unit: resolvedUnit,
        value: rawValue,
        formattedValue,
        quality,
      });
    }
    return result;
  }, [sourceValues, initialLoaded, variables, singleSignal, getProcessedValues]);

  const linearData = useMemo(() => {
    if (data && sortedThresholds) {
      return data.reduce((acc, current) => {
        let valueFill = 'grey';
        const sizedThresholds = sortedThresholds.map((threshold, index, array) => ({
          ...threshold,
          size:
            index === 0
              ? getThresholdValue(threshold)[0]
              : getThresholdValue(threshold) - getThresholdValue(array[index - 1]),
        }));

        const filteredThresholds = sizedThresholds.filter(
          (th) => getThresholdValue(th) > current.value
        );

        if (filteredThresholds.length > 0) {
          filteredThresholds[0].size = getThresholdValue(filteredThresholds[0]) - current.value;

          // if there's a threshold bigger than current value, use previous threshold color
          const nextThresholdIndex = sortedThresholds.findIndex(
            (th) => getThresholdValue(th) === getThresholdValue(filteredThresholds[0])
          );
          if (sortedThresholds[nextThresholdIndex - 1]) {
            valueFill = resolveColor(sortedThresholds[nextThresholdIndex - 1].fill);
          }
        }

        const lastThreshold = sortedThresholds.at(-1);
        if (current.value >= getThresholdValue(lastThreshold) && lastThreshold?.fill) {
          valueFill = resolveColor(lastThreshold.fill);
        }

        // Add formatted value with unit for labels
        const formattedValueWithUnit = `${current.formattedValue} ${current.unit}`;

        // Ensure unique name for React keys by appending acc.length (index)
        const uniqueName = current.name ?? `bar-${acc.length}`;

        return [...acc, { ...current, name: uniqueName, valueFill, formattedValueWithUnit }];
      }, []);
    }
    return [];
  }, [data, sortedThresholds, getThresholdValue]);

  const sortedData = useMemo(() => data?.toSorted((a, b) => a.value - b.value) || [], [data]);
  const dataRange = useMemo(
    () => [sortedData[0]?.value || 0, sortedData.at(-1)?.value || 0],
    [sortedData]
  );

  // invalidate if quality !== 1 (valid) or no data connectivity (disconnected)
  const isDataInvalid =
    data?.some((signalPoint) => signalPoint.quality > 1) ||
    (initialLoaded && variables.length > 0 && data?.length === 0) ||
    disconnected;

  /*
   * For area charts we transform data to include the stacked sizes of thresholds
   * as keyed values (keyedBars below), as well as returning a separate array of
   * thresholds that is used to iterate over to add the threshold specific fill.
   */

  const { areaData, areaThresholds } = useMemo(() => {
    if (data && sortedThresholds && !isLinearThresholdType) {
      return data.reduce(
        (acc, current) => {
          let valueFill = 'grey';
          const sizedBars = [];
          sortedThresholds.forEach((threshold, index, array) => {
            const [thresholdMin, thresholdMax] = getThresholdValue(threshold);
            const [dataMin] = dataRange;
            const previousThreshold = array[index - 1];
            const thresholdSize = thresholdMax - thresholdMin;

            if (index === 0) {
              if (dataMin < thresholdMin) {
                sizedBars.push({ min: dataMin, max: thresholdMin, size: thresholdMin - dataMin });
              }
              sizedBars.push({ ...threshold, size: thresholdSize });
            } else if (thresholdMin < previousThreshold.max) {
              if (thresholdMax > previousThreshold.max) {
                sizedBars.push({
                  ...threshold,
                  min: previousThreshold.max,
                  size: thresholdMax - previousThreshold.max,
                });
              }
            } else {
              if (thresholdMin > previousThreshold.max) {
                sizedBars.push({
                  min: previousThreshold.max,
                  max: thresholdMin,
                  size: thresholdMin - previousThreshold.max,
                });
              }
              sizedBars.push({ ...threshold, size: thresholdSize });
            }
          });

          const filteredBars = sizedBars.filter((b) => b.max >= current.value);

          if (filteredBars.length > 0) {
            filteredBars[0].size = filteredBars[0].max - current.value;
            valueFill = resolveColor(filteredBars[0].fill) || 'grey';
          }

          const keyedBars = Object.fromEntries(
            filteredBars.map((currentTh) => [`th-${currentTh.max}`, currentTh.size])
          );

          // Add formatted value with unit for labels
          const formattedValueWithUnit = `${current.formattedValue} ${current.unit}`;

          // Ensure unique name for React keys by appending array index
          const uniqueName = current.name ?? `bar-${acc.areaData.length}`;

          return {
            areaData: [
              ...acc.areaData,
              { ...current, name: uniqueName, ...keyedBars, valueFill, formattedValueWithUnit },
            ],
            // Only set areaThresholds once - use sizedBars from first iteration
            areaThresholds: acc.areaThresholds.length > 0 ? acc.areaThresholds : sizedBars,
          };
        },
        { areaData: [], areaThresholds: [] }
      );
    }
    return { areaData: [], areaThresholds: [] };
  }, [data, sortedThresholds, isLinearThresholdType, dataRange, getThresholdValue]);

  const barMax = Math.max(sortedThresholds?.at(-1)?.max || 0, sortedData?.at(-1)?.value || 0);

  const getPaddedValue = useCallback(
    (value) => {
      if (width < 500) {
        return (value * 100) / 75;
      }
      if (width < 1000) {
        return (value * 100) / 85;
      }
      return (value * 100) / 90;
    },
    [width]
  );

  const dataDomain = useMemo(() => {
    if (!isLinearThresholdType && sortedThresholds?.length) {
      return [sortedThresholds.at(0).min, sortedThresholds.at(-1).max];
    }
    const numberDigitsInMax = Math.floor(barMax).toString().length;
    let padX = 2;
    if (numberDigitsInMax > 1) {
      padX = 5 * 10 ** (numberDigitsInMax - 2);
    }
    const domainMax = getPaddedValue(barMax);
    // adjust to move up to the nearest sensible padX factor
    const adjustedDomainMax = Math.ceil(domainMax / padX) * padX;
    return ['dataMin', adjustedDomainMax];
  }, [barMax, isLinearThresholdType, sortedThresholds, getPaddedValue]);

  const barChart = isLinearThresholdType ? (
    <BarChart
      data={linearData}
      layout={configuration.layout}
      maxBarSize={width / 8}
    >
      <XAxis
        type={isVertical ? 'number' : 'category'}
        dataKey={isVertical ? null : 'name'}
        ticks={
          isVertical
            ? sortedThresholds?.map((th) => th.value)
            : linearData.map((d) => d.name || null)
        }
        minTickGap={0}
        tick={{ fontSize: 11 }}
        domain={isVertical && dataDomain}
      />
      <YAxis
        type={isVertical ? 'category' : 'number'}
        dataKey={isVertical ? 'name' : null}
        ticks={
          isVertical
            ? linearData.map((d) => d.name || null)
            : sortedThresholds?.map((th) => th.value)
        }
        minTickGap={0}
        tick={{ fontSize: 11 }}
        domain={isVertical ? ['dataMin', 'dataMax'] : dataDomain}
      />
      <Bar
        stackId="a"
        dataKey="value"
        background={true} // background color is set in css
        isAnimationActive={false} // Disable animation for <LabelList /> to display. Bugfix
      >
        {linearData.map(({ valueFill }, index) => (
          <Cell
            key={`cell-${index}`}
            fill={`${valueFill}`}
          />
        ))}
      </Bar>
      {sortedThresholds?.map((threshold, index) => (
        <ReferenceLine
          key={`th-${index}-${threshold.value}-${threshold.fill}`}
          y={isVertical ? null : `${threshold.value}`}
          x={isVertical ? `${threshold.value}` : null}
          stroke={darkenThresholdColor(resolveColor(threshold.fill))}
          strokeWidth={2}
        />
      ))}
      <Bar
        stackId="a"
        dataKey="value"
        fill="transparent"
        isAnimationActive={false}
        tooltipType="none"
      />
      <Tooltip
        allowEscapeViewBox={{ x: false, y: true }}
        cursor={{ fill: 'transparent' }}
        wrapperStyle={{ zIndex: '1000' }}
        contentStyle={{ padding: '0.25rem 0.5rem' }}
        labelFormatter={() => ''}
        formatter={tooltipFormatter}
      />
      <Legend
        content={
          <CustomLegend
            thresholdsData={sortedThresholds}
            isLinearThresholdType={isLinearThresholdType}
            isVertical={isVertical}
          />
        }
      />
    </BarChart>
  ) : (
    <BarChart
      data={areaData}
      margin={isVertical ? { top: 0, right: 0, left: 15 } : { top: 15, right: 0 }}
      maxBarSize={width / 8}
      layout={configuration.layout}
    >
      <XAxis
        type={isVertical ? 'number' : 'category'}
        dataKey={isVertical ? null : 'name'}
        tick={{ fontSize: 11 }}
        minTickGap={0}
        domain={isVertical && dataDomain}
      />
      <YAxis
        type={isVertical ? 'category' : 'number'}
        dataKey={isVertical ? 'name' : null}
        tick={{ fontSize: 11 }}
        minTickGap={0}
        domain={isVertical ? ['dataMin', 'dataMax'] : dataDomain}
      />
      <Bar
        stackId="a"
        dataKey="value"
        background
      >
        {areaData.map(({ valueFill }, index) => (
          <Cell
            key={`cell-${index}`}
            fill={valueFill}
          />
        ))}
      </Bar>
      {areaThresholds.map((threshold, index) => (
        <Bar
          key={`th-${index}-${threshold.max || threshold.min}-${threshold.fill}`}
          stackId="a"
          dataKey={`th-${threshold.max}`}
          fill={threshold.fill ? `${resolveColor(threshold.fill)}25` : 'transparent'}
          name={
            threshold.description
              ? `${threshold.description} (${index + 1})`
              : `Threshold ${index + 1}`
          }
          tooltipType="none"
        />
      ))}
      <Tooltip
        allowEscapeViewBox={{ x: false, y: true }}
        cursor={{ fill: 'transparent' }}
        wrapperStyle={{ zIndex: '1000' }}
        contentStyle={{ padding: '0.25rem 0.5rem' }}
        labelFormatter={() => ''}
        formatter={tooltipFormatter}
      />
      <Legend
        content={
          <CustomLegend
            thresholdsData={sortedThresholds}
            isLinearThresholdType={isLinearThresholdType}
            isVertical={isVertical}
          />
        }
      />
    </BarChart>
  );

  const vizHeight = height - 28; // less header
  const loaderHeight = vizHeight - 28; // less loader padding

  const classes = classnames('menu', {});

  const hasValidData = data?.some(
    (item) =>
      item?.value !== null && item?.value !== undefined && !Number.isNaN(Number(item?.value))
  );
  const isVizEmpty = !hasValidData && !loading && initialLoaded;
  const showOptionsMenu = !isPreview && !isVizEmpty;

  return (
    <div
      className={`bar-gauge-visualization-component ${isDataInvalid ? 'invalid-data' : ''}`}
      ref={clientReference}
    >
      {!isFullscreen && (
        <div className="bar-gauge-header">
          <div className="bar-gauge-title viz-wrapper">
            <span className="title viz-name">
              <LabelWithTooltip name={configuration?.title || visualization?.name || '\u00A0'} />
            </span>
            <span className="viz-help">{isDataInvalid ? <DegradedQualityIndicator /> : null}</span>
          </div>
          {!isPreview && (
            <div className={classes}>
              {initialLoaded && loading && (
                <Spinner
                  size="s"
                  className="spinner"
                />
              )}
              {showOptionsMenu && (
                <DropdownMenu
                  trigger="he-moreoptionsvertical"
                  triggerSize="s"
                  menuOptions={getMoreOptions({ onFullscreen })}
                  menuXPlacement={dropdownPlacement?.x}
                  menuYPlacement={dropdownPlacement?.y}
                  styles={{ dropdownIndicator: { padding: '0px' }, option: { cursor: 'pointer' } }}
                />
              )}
            </div>
          )}
        </div>
      )}
      {!initialLoaded && loading ? (
        <Loader
          text="Loading ..."
          height={loaderHeight}
        />
      ) : (
        <div className="bar-gauge-wrapper">
          {singleSignalTypeError ? (
            <div
              className="single-signal-error"
              style={{ height: vizHeight }}
            >
              <Icon
                icon="he-info"
                size="s"
                fill="gray"
              />
              <p className="error-message">{singleSignalTypeError}</p>
            </div>
          ) : (
            <ResponsiveContainer
              width="100%"
              height={vizHeight}
            >
              {barChart}
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
};

export default BarGaugeVisualization;
